import { useMutation, useQuery } from "@tanstack/react-query";
import { useState, type FormEvent, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { Navigate, useLocation } from "react-router";

import { useAuth } from "@/app/auth/context";
import { ServiceUnavailablePage } from "@/components/ServiceProblemNotice";
import { StudioSignature } from "@/components/brand/StudioSignature";
import { VaultMark } from "@/components/brand/VaultMark";
import { Button } from "@/components/ui/Button";
import { Alert } from "@/components/ui/Feedback";
import { TextField } from "@/components/ui/Field";
import { ApiError, getHealth } from "@/lib/api";
import { login, verifyTotp } from "@/lib/auth-api";
import { parseApiDate } from "@/lib/format";
import { healthProblem, serviceProblem } from "@/lib/service-problems";
import { formatCountdown, useSecondsLeft } from "@/lib/use-seconds-left";

import { ArchiveMotif } from "./ArchiveMotif";
import { PasswordChangeForm } from "./PasswordChangeForm";
import { TotpSetupPanel } from "./TotpSetupPanel";

function StepCard({ title, body, children }: { title: string; body?: ReactNode; children: ReactNode }) {
  return (
    <div className="w-full max-w-sm">
      <h1 className="text-[1.6rem] leading-tight font-semibold">{title}</h1>
      {body && <p className="mt-2 text-sm text-ink-muted">{body}</p>}
      <div className="mt-7">{children}</div>
    </div>
  );
}

function CredentialsStep({ onDone }: { onDone: () => void }) {
  const { t } = useTranslation();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [lockEnds, setLockEnds] = useState<number | null>(null);
  const mutation = useMutation({
    mutationFn: () => login(username, password),
    onSuccess: onDone,
    onMutate: () => setLockEnds(null),
    onError: (failure) => {
      const retryAfter = failure instanceof ApiError ? failure.detail.retry_after : undefined;
      if (typeof retryAfter === "number") setLockEnds(Date.now() + retryAfter * 1000);
    },
  });
  const lockSeconds = useSecondsLeft(lockEnds);

  let error: ReactNode = null;
  let notice: string | null = null;
  const problem = serviceProblem(mutation.error);
  if (problem) error = t(`service.${problem}.title`);
  else if (lockEnds !== null) {
    if (lockSeconds > 0) {
      error = (
        <>
          {t("auth.lockedNow")} {/* A timer is not announced on every tick, unlike the alert around it. */}
          <span role="timer" className="font-medium tabular-nums">
            {t("auth.retryIn", { time: formatCountdown(lockSeconds) })}
          </span>
        </>
      );
    } else notice = t("auth.lockOver");
  } else if (mutation.error instanceof ApiError) {
    const { code, detail } = mutation.error;
    if (code === "locked" && typeof detail.until === "string") {
      const time = new Intl.DateTimeFormat("it-IT", { timeStyle: "short" }).format(
        parseApiDate(detail.until),
      );
      error = t("auth.locked", { time });
    } else if (code === "rate_limited") error = t("auth.rateLimited");
    else if (code === "invalid_credentials") error = t("auth.invalidCredentials");
    else error = t("common.errorGeneric");
  } else if (mutation.isError) error = t("common.errorGeneric");

  const onSubmit = (event: FormEvent) => {
    event.preventDefault();
    mutation.mutate();
  };

  return (
    <StepCard title={t("auth.signInTitle")}>
      <form onSubmit={onSubmit} className="space-y-4">
        {error && <Alert>{error}</Alert>}
        {notice && <Alert tone="primary">{notice}</Alert>}
        <TextField
          label={t("auth.username")}
          autoComplete="username"
          autoCapitalize="none"
          spellCheck={false}
          value={username}
          onChange={(e) => setUsername(e.target.value)}
          required
          autoFocus
        />
        <TextField
          label={t("auth.password")}
          type="password"
          autoComplete="current-password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          required
        />
        <Button type="submit" busy={mutation.isPending} disabled={lockSeconds > 0} className="w-full">
          {t("auth.signIn")}
        </Button>
      </form>
    </StepCard>
  );
}

function MfaStep({ onDone }: { onDone: () => void }) {
  const { t } = useTranslation();
  const [code, setCode] = useState("");
  const mutation = useMutation({ mutationFn: () => verifyTotp(code), onSuccess: onDone });
  const onSubmit = (event: FormEvent) => {
    event.preventDefault();
    mutation.mutate();
  };
  return (
    <StepCard title={t("auth.mfaTitle")} body={t("auth.mfaBody")}>
      <form onSubmit={onSubmit} className="space-y-4">
        {mutation.isError && <Alert>{t("auth.invalidCode")}</Alert>}
        <TextField
          label={t("auth.code")}
          inputMode="numeric"
          autoComplete="one-time-code"
          maxLength={6}
          value={code}
          onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
          required
          autoFocus
        />
        <Button type="submit" busy={mutation.isPending} disabled={code.length !== 6} className="w-full">
          {t("auth.verify")}
        </Button>
      </form>
    </StepCard>
  );
}

export function LoginPage() {
  const { t } = useTranslation();
  const { status, me, problem, refresh, signOut } = useAuth();
  const location = useLocation();
  const from = (location.state as { from?: string } | null)?.from ?? "/";
  // Visitors are not signed in yet, so nothing has touched the database: ask the server about it directly.
  const health = useQuery({
    queryKey: ["health"],
    queryFn: getHealth,
    refetchInterval: (query) =>
      healthProblem(query.state.data?.database) || serviceProblem(query.state.error) ? 10_000 : false,
  });
  const infrastructure = problem ?? healthProblem(health.data?.database) ?? serviceProblem(health.error);

  if (status === "active") return <Navigate to={from} replace />;
  if (infrastructure) {
    return (
      <ServiceUnavailablePage
        problem={infrastructure}
        onRetry={() => {
          void refresh();
          void health.refetch();
        }}
      />
    );
  }

  const advance = () => void refresh();
  let step: ReactNode = <CredentialsStep onDone={advance} />;
  if (status === "step" && me) {
    if (me.state === "mfa") step = <MfaStep onDone={advance} />;
    else if (me.state === "password_change")
      step = (
        <StepCard title={t("auth.passwordChangeTitle")} body={t("auth.passwordChangeBody")}>
          <PasswordChangeForm minLength={me.password_min_length} onDone={advance} />
        </StepCard>
      );
    else if (me.state === "mfa_setup")
      step = (
        <StepCard title={t("auth.setupTitle")} body={`${t("auth.setupRequired")} ${t("auth.setupBody")}`}>
          <TotpSetupPanel onDone={advance} />
        </StepCard>
      );
  }

  return (
    <div className="grid min-h-dvh bg-canvas lg:grid-cols-[minmax(0,5fr)_minmax(0,6fr)]">
      <aside className="relative hidden flex-col justify-between overflow-hidden border-r border-white/5 bg-[#151b22] p-12 text-white lg:flex dark:bg-[#0c1015]">
        <VaultMark className="size-11" label={t("app.name")} />
        <div className="space-y-10">
          <p className="max-w-md text-[2rem] leading-[1.15] font-semibold tracking-tight text-balance">
            {t("auth.brandTagline")}
          </p>
          <ArchiveMotif />
        </div>
        <StudioSignature className="text-white/60 [&_.font-medium]:text-white/80" />
      </aside>

      <main id="main" className="flex flex-col items-center justify-center px-4 py-10 sm:px-8">
        <VaultMark className="mb-10 size-12 lg:hidden" label={t("app.name")} />
        {step}
        {status === "step" && (
          <button
            type="button"
            onClick={() => void signOut()}
            className="mt-6 text-sm text-ink-muted underline-offset-4 hover:text-ink hover:underline"
          >
            {t("auth.signOut")}
          </button>
        )}
        <StudioSignature className="mt-12 lg:hidden" />
      </main>
    </div>
  );
}
